import {
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
  Typography,
  Modal,
  Box,
  TablePagination,
} from "@mui/material";
import { ComoInput } from "../Common/ComoInput";
import {
  useWebsites,
  useStopWebsite,
  useUploadWebsite,
  useDeleteWebsite,
  useDownloadWebsiteContent,
  useRescrapeWebsite,
} from "../../Query/useCompanies";
import { useState, useEffect } from "react";
import LanguageIcon from "@mui/icons-material/Language";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useTranslation } from "react-i18next";
import { downloadDocumentBlob } from "../../Helpers/documentHelper";
import { ProcessingStatus } from "../../Enums/companyStatus";
import color from "../../Constants/colors";
import { Website } from "../../Models/website";
import React from "react";

interface WebsiteUploadProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
}

const WebsiteUpload: React.FC<WebsiteUploadProps> = ({
  open,
  onClose,
  companyId,
}) => {
  const [page, setPage] = useState(0);
  const { t } = useTranslation("websiteUpload");
  const pageSize = 10;
  const [sortField] = useState("created_at");
  const [sortOrder] = useState<"asc" | "desc">("desc");

  const { data: websitesData, isLoading } = useWebsites(
    companyId,
    page + 1,
    pageSize,
    sortField,
    sortOrder,
    "",
    open,
  );

  const stopWebsiteMutation = useStopWebsite(companyId);
  const uploadWebsiteMutation = useUploadWebsite(companyId);
  const deleteWebsiteMutation = useDeleteWebsite(companyId);
  const downloadWebsiteContentMutation = useDownloadWebsiteContent();
  const rescrapeWebsiteMutation = useRescrapeWebsite(companyId);

  const [websiteAddress, setWebsiteAddress] = useState("");
  const [uploadStep, setUploadStep] = useState<
    "input" | "processing" | "success"
  >("input");

  useEffect(() => {
    if (uploadWebsiteMutation.isPending) {
      setUploadStep("processing");
    } else if (uploadWebsiteMutation.isSuccess) {
      setUploadStep("success");
      const timer = setTimeout(() => {
        setUploadStep("input");
        uploadWebsiteMutation.reset();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [
    uploadWebsiteMutation,
    uploadWebsiteMutation.isPending,
    uploadWebsiteMutation.isSuccess,
  ]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleUploadWebsite = () => {
    if (!websiteAddress) return;
    uploadWebsiteMutation.mutate(
      { websiteName: websiteAddress, websiteAddress },
      {
        onSuccess: () => {
          setWebsiteAddress("");
        },
      },
    );
  };

  const handleDeleteWebsite = async (website_id: number) => {
    deleteWebsiteMutation.mutate(website_id);
  };

  const handleDownloadScrappedWebsiteContent = (
    websiteId: number,
    name: string,
  ) => {
    downloadWebsiteContentMutation.mutate(
      { websiteId },
      {
        onSuccess: (response) => {
          downloadDocumentBlob(response, `${name}.txt`);
        },
      },
    );
  };

  const getStatusIcon = (status: number) => {
    switch (status) {
      case ProcessingStatus.IN_PROGRESS:
        return (
          <CircularProgress size={16} sx={{ color: color.accent, mr: 1 }} />
        );
      case ProcessingStatus.PROCESSED:
        return (
          <CheckCircleOutlineIcon
            sx={{ color: color.green, fontSize: 20, mr: 1 }}
          />
        );
      case ProcessingStatus.ERROR:
      case ProcessingStatus.TIME_LIMIT_EXCEEDED:
        return (
          <ErrorOutlineIcon sx={{ color: color.red, fontSize: 20, mr: 1 }} />
        );
      default:
        return (
          <LanguageIcon
            sx={{ color: color.primary_dark, fontSize: 20, mr: 1 }}
          />
        );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        padding: "20px",
      }}
    >
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: "8px",
          border: `1px solid ${color.gray200}`,
          width: "100%",
          maxWidth: "900px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
            borderBottom: `1px solid ${color.gray200}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <LanguageIcon sx={{ color: color.accent }} />
            <Typography
              variant="h6"
              sx={{
                margin: 0,
                fontSize: "1.25rem",
                fontWeight: 600,
                color: color.primary_dark,
              }}
            >
              {t("websiteUpload.websites")}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              padding: "8px",
              borderRadius: "4px",
              "&:hover": { backgroundColor: color.gray100 },
            }}
          >
            <CloseIcon sx={{ color: color.gray500 }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            flex: 1,
            overflow: "hidden",
            minHeight: "450px",
          }}
        >
          <Box
            sx={{
              flex: "1 1 50%",
              padding: "32px",
              borderRight: `1px solid ${color.gray200}`,
              display: "flex",
              flexDirection: "column",
              backgroundColor: color.gray50,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                mb: 3,
                fontWeight: 600,
                color: color.primary_dark,
              }}
            >
              {t("websiteUpload.uploadNewWebsite")}
            </Typography>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              {uploadStep === "input" && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <ComoInput
                    value={websiteAddress}
                    onChange={(e) => setWebsiteAddress(e.target.value)}
                    placeholder="https://example.com"
                    primaryColor={color.primary_dark}
                    multiline={false}
                    maxRows={1}
                    size="medium"
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleUploadWebsite}
                    disabled={
                      !websiteAddress || uploadWebsiteMutation.isPending
                    }
                    sx={{
                      py: 1.5,
                      backgroundColor: color.accent,
                      color: color.white,
                      "&:hover": {
                        backgroundColor: color.accent_dark,
                        color: color.white,
                      },
                      "&.Mui-disabled": {
                        backgroundColor: color.btn_dark_disabled,
                        color: "rgba(255, 255, 255, 0.7)",
                      },
                      fontWeight: 600,
                      boxShadow: "none",
                    }}
                  >
                    {t("websiteUpload.scrape")}
                  </Button>
                </Box>
              )}

              {uploadStep === "processing" && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress
                    size={48}
                    sx={{ color: color.accent, mb: 2 }}
                  />
                  <Typography
                    variant="body1"
                    sx={{ color: color.primary_dark, fontWeight: 500 }}
                  >
                    {t("websiteUpload.processing")}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: color.gray500, mt: 1 }}
                  >
                    {t("websiteUpload.websiteAnalyzing")}
                  </Typography>
                </Box>
              )}

              {uploadStep === "success" && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <CheckCircleOutlineIcon
                    sx={{ color: color.green, fontSize: 64, mb: 2 }}
                  />
                  <Typography
                    variant="h6"
                    sx={{ color: color.primary_dark, fontWeight: 600 }}
                  >
                    {t("websiteUpload.successfullyStarted")}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: color.gray500, mt: 1 }}
                  >
                    {t("websiteUpload.addedToQueue")}
                  </Typography>
                  <Button
                    sx={{
                      mt: 3,
                      color: color.accent,
                      "&:hover": { backgroundColor: "rgba(242, 141, 1, 0.08)" },
                    }}
                    onClick={() => setUploadStep("input")}
                  >
                    {t("websiteUpload.addAnotherWebsite")}
                  </Button>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                mt: "auto",
                pt: 2,
                borderTop: `1px solid ${color.gray200}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: color.gray500,
                  whiteSpace: "nowrap",
                  display: "block",
                }}
              >
                {t("websiteUpload.processingNote")}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              flex: "1 1 50%",
              padding: "32px",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                mb: 3,
                fontWeight: 600,
                color: color.primary_dark,
              }}
            >
              {t("websiteUpload.existingWebsites")}
            </Typography>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
                pr: 1,
              }}
            >
              {isLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    flex: 1,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress sx={{ color: color.accent }} />
                </Box>
              ) : websitesData?.websites && websitesData.websites.length > 0 ? (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {websitesData.websites.map((website: Website) => (
                    <Box
                      key={website.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: "white",
                        borderRadius: "8px",
                        border: `1px solid ${color.gray200}`,
                        padding: "16px",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: color.accent,
                          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                          backgroundColor: `${color.accent}05`,
                        },
                      }}
                    >
                      {getStatusIcon(website.status)}

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: color.primary_dark,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {website.web_adress}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          ml: 2,
                        }}
                      >
                        {website.status === ProcessingStatus.IN_PROGRESS ? (
                          <Tooltip title={t("websiteUpload.stopProcessing")}>
                            <IconButton
                              size="small"
                              onClick={() =>
                                stopWebsiteMutation.mutate(website.id)
                              }
                              sx={{
                                color: color.red,
                                "&:hover": { backgroundColor: color.red50 },
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <>
                            {website.status === ProcessingStatus.PROCESSED && (
                              <Tooltip
                                title={t("websiteUpload.downloadScrapedData")}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleDownloadScrappedWebsiteContent(
                                      website.id,
                                      website.name,
                                    )
                                  }
                                  sx={{
                                    color: color.accent,
                                    "&:hover": {
                                      backgroundColor: color.blue50,
                                    },
                                  }}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title={t("websiteUpload.rescrape")}>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  rescrapeWebsiteMutation.mutate(website.id)
                                }
                                disabled={rescrapeWebsiteMutation.isPending}
                                sx={{
                                  color: color.accent,
                                  "&:hover": { backgroundColor: color.blue50 },
                                }}
                              >
                                <RefreshIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}

                        <Tooltip title={t("websiteUpload.delete")}>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteWebsite(website.id)}
                            disabled={
                              website.status === ProcessingStatus.IN_PROGRESS
                            }
                            sx={{
                              color: color.gray400,
                              "&:hover": {
                                color: color.red,
                                backgroundColor: color.red50,
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flex: 1,
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: color.gray50,
                    borderRadius: "8px",
                    border: `1px dashed ${color.gray300}`,
                    textAlign: "center",
                    p: 4,
                  }}
                >
                  <Typography variant="body2" sx={{ color: color.gray500 }}>
                    {t("websiteUpload.noWebsitesAvailable")}
                  </Typography>
                </Box>
              )}
            </Box>

            {websitesData && websitesData.total > pageSize && (
              <TablePagination
                component="div"
                count={websitesData.total}
                rowsPerPage={pageSize}
                page={page}
                rowsPerPageOptions={[]}
                onPageChange={handleChangePage}
                sx={{
                  mt: 2,
                  "& .MuiTablePagination-toolbar": {
                    minHeight: 48,
                    color: color.primary_dark,
                  },
                  color: color.primary_dark,
                  "& .MuiTablePagination-displayedRows": {
                    color: color.primary_dark,
                  },
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default WebsiteUpload;
